import React from "react";
import Image from "next/image";
import type { ChakraProps } from "@chakra-ui/react";
import { Flex, Text, Heading, Button, Grid, GridItem } from "@chakra-ui/react";
import type { ListProps } from "components/List";
import { List } from "components/List";
import { Link } from "components/Link";
import type { UseScreenFromBreakpoints } from "lib/checkViewport";
import { useScreenFromBreakpoints } from "lib/checkViewport";

const getVariantFromDeviceScreen = ({
  isTiny,
  isMobile
}: UseScreenFromBreakpoints): [string, string] => {
  if (isTiny || isMobile) return ["demiM1", "bookM1"];

  return ["bookM0", "book0"];
};

type LinksColumn = ListProps & {
  colSpan?: {
    base: number;
    md: number;
  };
};

type Props = ChakraProps & {
  title?: string;
  content: {
    introLine: string;
    headline: string;
  };
  links: LinksColumn[];
};

export const ErrorPage: React.FC<Props> = ({ content, links }) => {
  const screen = useScreenFromBreakpoints();
  const [variantTitle, variantText] = getVariantFromDeviceScreen(screen);

  const listProps = {
    pt: { base: 3, md: 7 },
    rowStart: { base: 3, md: 2 }
  };

  return (
    <>
      <Grid
        w="full"
        p={{ base: 1, md: 2 }}
        gridTemplateColumns={{ base: "1fr 1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
      >
        <GridItem flexFlow="column" colSpan={{ base: 5, md: 8 }}>
          <Text
            variant="book1"
            data-testid="error-page-text"
            mt={{ base: 2, md: 7 }}
          >
            {content.introLine}
          </Text>
          <Heading as="h1" variant="caps4" mt={{ base: 1.5, md: 2 }}>
            {content.headline}
          </Heading>
        </GridItem>
        {links.map(({ heading, items, colSpan }) => {
          return (
            <GridItem
              key={heading.substring(0, 4)}
              {...listProps}
              colSpan={colSpan}
            >
              <List
                heading={heading}
                items={items}
                variant="textLinkNoUnderline"
                variantTitle={variantTitle}
                variantText={variantText}
              />
            </GridItem>
          );
        })}
        <GridItem
          colSpan={{ base: 3, md: 2 }}
          gridColumnStart={{ base: 1 }}
          gridRowStart={{ base: 2, md: 3 }}
          minHeight={50}
        >
          <Flex
            position="relative"
            w="full"
            height={{ base: 140, sm: 245, md: 140 }}
            mt={{ base: 3, md: "-3rem" }}
          >
            <Image
              src="/images/error.jpg"
              alt="404 image"
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              style={{
                objectFit: "contain",
                objectPosition: "top"
              }}
              priority
            />
          </Flex>
        </GridItem>
        <GridItem
          colSpan={{ base: 3, md: 2 }}
          gridColumnStart={{ base: "1 !important" }}
          gridRowStart={{ base: 4, md: 3 }}
        >
          <Text variant="book0" mt={{ base: 1 }} data-testid="error-page-text">
            If you can’t find your way out, email us at
          </Text>
          <Text variant="demi0" mt={0}>
            customer.service@beautypie.com
          </Text>
        </GridItem>
        <GridItem
          colSpan={{ base: 3, md: 2 }}
          textAlign={{ base: "center", md: "left" }}
          gridRowStart={{ base: 5, md: 4 }}
          pt={{ base: 5, md: 1 }}
          pb={{ base: 5, md: 6 }}
        >
          <Link href="/contact">
            <Button variant="primary" mt={0}>
              Contact us
            </Button>
          </Link>
        </GridItem>
      </Grid>
    </>
  );
};
